import { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, platformAuthenticatorIsAvailable, } from '@simplewebauthn/browser';
const PENDING_ENROLLMENT_KEY = 'paperair.auth.pendingEnrollmentToken';
export function isSupported() {
    return browserSupportsWebAuthn();
}
export function isAutofillSupported() {
    return browserSupportsWebAuthnAutofill();
}
/**
 * Whether a built-in, user-verifying authenticator is available. Never rejects: a browser whose
 * own check throws (seen in some embedded browsers) reports false.
 */
export async function isPlatformAuthenticatorAvailable() {
    try {
        return await platformAuthenticatorIsAvailable();
    }
    catch {
        return false;
    }
}
/**
 * `PublicKeyCredential.getClientCapabilities()` when the browser has it, otherwise null. Never
 * rejects.
 */
export async function getClientCapabilities() {
    if (!isSupported()) {
        return null;
    }
    const PKC = PublicKeyCredential;
    if (typeof PKC.getClientCapabilities !== 'function') {
        return null;
    }
    try {
        return await PKC.getClientCapabilities();
    }
    catch {
        return null;
    }
}
let supportPromise = null;
/**
 * All device checks in one call, computed once per page load (capabilities don't change during
 * a page session). Prefers `getClientCapabilities()` when the browser has it and falls back to
 * the individual checks otherwise. Never rejects.
 */
export function getSupport() {
    if (!supportPromise) {
        supportPromise = computeSupport();
    }
    return supportPromise;
}
async function computeSupport() {
    if (!isSupported()) {
        return { webauthn: false, autofill: false, platformAuthenticator: false, hybrid: false, capabilities: null };
    }
    const capabilities = await getClientCapabilities();
    const autofill = capabilities?.conditionalGet ?? (await isAutofillSupported().catch(() => false));
    const platformAuthenticator = capabilities?.passkeyPlatformAuthenticator ??
        capabilities?.userVerifyingPlatformAuthenticator ??
        (await isPlatformAuthenticatorAvailable());
    return {
        webauthn: true,
        autofill,
        platformAuthenticator,
        hybrid: capabilities?.hybridTransport ?? null,
        capabilities,
    };
}
/**
 * Whether passkeys can work on this host: the browser refuses any ceremony whose RP ID isn't the
 * page's host or a parent domain of it. Pass the `rpId` from `getConfig()`. Checking first lets
 * an app hide passkey UI cleanly on a misconfigured or mismatched environment (e.g. a staging
 * host outside staging auth's RP ID) instead of failing every ceremony.
 */
export function isRpIdValidForHost(rpId, hostname = globalThis.location?.hostname ?? '') {
    const host = hostname.toLowerCase().replace(/\.$/, '');
    const id = rpId.toLowerCase().replace(/\.$/, '');
    if (!host || !id) {
        return false;
    }
    return host === id || host.endsWith(`.${id}`);
}
/**
 * Stashes an enrollment token across a login redirect: the confirm-link page opens without a
 * passport session, sends the user to login, and needs the token back afterward to resume the
 * ceremony (build plan 4.1, "Session requirement on step 3").
 */
export function stashPendingEnrollment(token) {
    try {
        sessionStorage.setItem(PENDING_ENROLLMENT_KEY, token);
    }
    catch {
        // Storage unavailable (private mode, disabled storage) — the resume flow degrades to
        // asking the user to click the email link again after logging in.
    }
}
/**
 * Reads and clears the stashed enrollment token, if any.
 */
export function takePendingEnrollment() {
    try {
        const token = sessionStorage.getItem(PENDING_ENROLLMENT_KEY);
        if (token) {
            sessionStorage.removeItem(PENDING_ENROLLMENT_KEY);
        }
        return token;
    }
    catch {
        return null;
    }
}
